import { Button } from '@client/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu';
import { UnderwritingStatus } from '@client/features/underwriting-eligibility';
import { AlertCircle, ArrowRight, Loader2, MoreHorizontal, WandSparkles } from 'lucide-react';
import React from 'react';

interface StepContentProps {
  children: React.ReactNode;
  className?: string;
}

export function ApplicationStepContent({ children, className = '' }: StepContentProps) {
  return <div className={`${className}`}>{children}</div>;
}

interface StepHeaderProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}

export function StepHeader({ icon, title, description, className = '' }: StepHeaderProps) {
  return (
    <div className={`mb-6 ${className}`}>
      {icon && <div className="mb-4 flex justify-center">{icon}</div>}
      <h2 className="mb-2 text-xl font-semibold text-gray-900">{title}</h2>
      <p className="mx-auto max-w-lg text-gray-700">{description}</p>
    </div>
  );
}

interface StepActionsProps {
  children: React.ReactNode;
  className?: string;
}

export function StepActions({ children, className = '' }: StepActionsProps) {
  return (
    <div className={`flex justify-end gap-4 ${className}`}>
      <div className="flex flex-wrap justify-end gap-4">{children}</div>
    </div>
  );
}

interface StepActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  variant?: 'default' | 'outline';
  className?: string;
  icon?: React.ReactNode;
  testId?: string;
}

export function StepActionButton({
  onClick,
  disabled = false,
  children,
  variant = 'default',
  className = '',
  icon,
  testId,
}: StepActionButtonProps) {
  return (
    <Button
      variant={variant}
      onClick={onClick}
      data-testid={testId}
      className={`px-8 py-4 text-base font-medium ${className}`}
      disabled={disabled}
    >
      {children}
      {icon ?? <ArrowRight className="ml-2 h-5 w-5" />}
    </Button>
  );
}

export function StepSkipButton({
  onClick,
  disabled = false,
  children,
  className = '',
  isLoading = false,
}: StepActionButtonProps & { isLoading?: boolean }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={`px-8 py-4 text-base font-medium ${className}`}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 size-5 animate-spin" /> Processing...
        </>
      ) : (
        children
      )}
    </Button>
  );
}

interface DropdownMenuAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  className?: string;
}

interface DiscreteActionsDropdownProps {
  actions: DropdownMenuAction[];
  triggerClassName?: string;
  menuClassName?: string;
  align?: 'start' | 'center' | 'end';
}

/**
 * Generic dropdown menu for discrete actions that should be less prominent
 *
 /**
  * @param actions - The actions to display in the dropdown menu
  * @param triggerClassName - The class name to apply to the trigger button
  * @param menuClassName - The class name to apply to the menu
  * @param align - The alignment of the menu
  */
export function DiscreteActionsDropdown({
  actions,
  triggerClassName = '',
  menuClassName = 'w-32',
  align = 'end',
}: DiscreteActionsDropdownProps) {
  if (actions.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-8 w-8 p-0 text-gray-500 hover:text-gray-700 ${triggerClassName}`}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open actions menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={menuClassName}
      >
        {actions.map(action => (
          <DropdownMenuItem
            key={action.id}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`text-sm text-gray-700 hover:text-gray-900 ${action.className ?? ''}`}
          >
            {action.isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {action.loadingText ?? 'Processing...'}
              </>
            ) : (
              <>
                {action.icon && <span className="mr-2">{action.icon}</span>}
                {action.label}
              </>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface DiscreteSkipDropdownProps {
  onSkip: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  label?: string;
  className?: string;
}

export function DiscreteSkipDropdown({
  onSkip,
  disabled = false,
  isLoading = false,
  label = 'Skip Step',
  className = '',
}: DiscreteSkipDropdownProps) {
  const skipAction: DropdownMenuAction = {
    id: 'skip',
    label,
    icon: <WandSparkles className="h-4 w-4" />,
    onClick: onSkip,
    disabled,
    isLoading,
    loadingText: 'Processing...',
  };

  return (
    <DiscreteActionsDropdown
      actions={[skipAction]}
      triggerClassName={className}
    />
  );
}

const ALERT_VARIANTS = {
  warning: {
    container: 'border-amber-200 bg-amber-50 text-amber-800',
    icon: 'text-amber-500',
  },
  error: {
    container: 'border-red-200 bg-red-50 text-red-800',
    icon: 'text-red-700',
  },
  info: {
    container: 'border-blue-200 bg-blue-50 text-blue-800',
    icon: 'text-blue-500',
  },
};

interface StepAlertAction {
  id?: string;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'ghost';
  disabled?: boolean;
  isLoading?: boolean;
}

interface StepAlertProps {
  title: string;
  description: string;
  variant?: 'warning' | 'error' | 'info';
  className?: string;
  actions?: StepAlertAction[];
}

export function StepAlert({
  title,
  description,
  variant = 'warning',
  className = '',
  actions,
}: StepAlertProps) {
  const variantConfig = ALERT_VARIANTS[variant];

  return (
    <div className={`mt-4 rounded-md border p-4 ${variantConfig.container} ${className}`}>
      <div className="flex items-start">
        <AlertCircle className={`mr-3 mt-0.5 h-5 w-5 flex-shrink-0 ${variantConfig.icon}`} />
        <div className="flex-1">
          <h3 className="text-sm font-medium">{title}</h3>
          <p className="mt-1 text-sm">{description}</p>
          {actions && actions.length > 0 && (
            <div className="mt-3 flex gap-2">
              {actions.map((action, index) => (
                <Button
                  key={action.id ?? action.label ?? index}
                  variant={action.variant || 'outline'}
                  size="sm"
                  onClick={action.onClick}
                  disabled={action.disabled || action.isLoading}
                  className="h-8"
                >
                  {action.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface StepAiAnalysisProps {
  productName: string;
  fundingPolicyType: FundingPolicyType;
  policyType?: string;
  className?: string;
}

export function StepAiAnalysis({
  productName,
  fundingPolicyType,
  policyType,
  className = '',
}: StepAiAnalysisProps) {
  return (
    <div className={`flex justify-center ${className}`}>
      <UnderwritingStatus
        productName={productName}
        fundingPolicyType={fundingPolicyType}
        policyType={policyType}
      />
    </div>
  );
}
